<template>
  <div class="body edit-panel">
    <div class="tb-form-group">
      <label class="tb-label">Layout Title</label>

      <input
        class="tb-input"
        type="text"
        v-model="title"
        data-test="asset-editor-title"
        @keyup.right.stop
        @keyup.left.stop
      />
    </div>
    <div class="flex column-gap-lg row-gap-sm flex-wrap margin-bottom-md">
      <div class="tb-form-group flex-grow margin-bottom-none">
        <label class="tb-label">Round</label>

        <select
          class="tb-select"
          data-test="asset-editor-fillType"
          v-model="currentRoundIndex"
        >
          <option
            v-for="(round, index) in rounds"
            :key="`round-${index}`"
            :value="index"
          >
            {{ round.title }}
          </option>
        </select>
      </div>

      <div>
        <label class="tb-label">Display State</label>
        <div
          class="poll-state"
        >
          <label
            v-for="state in pollStates"
            :key="state"
            class="poll-state__label"
            :class="{ 'poll-state__label--active': currentDisplayState === state }"
          >
            <input
              v-model="currentDisplayState"
              type="radio"
              :value="state"
              class="poll-state__input"
            />
            {{ stateLabels[state] || state }}
          </label>
        </div>
      </div>

      <div>
        <label class="tb-label">Question</label>
        <div class="data-list__page-controls data-list__page-controls--no-border">
          <button
            class="tb-btn tb-btn-contained medium-em"
            :disabled="currentQuestionIndex <= 1"
            data-test="graphics-editor-dynamicList-previousPage"
            @click="currentQuestionIndex--"
          >
            <span class="fas fa-backward" />
          </button>

          <input
            type="number"
            :min="1"
            :max="questions.length"
            v-model.number="currentQuestionIndex"
            class="tb-input flex-grow margin-left-md margin-right-md page-input"
            data-test="graphics-editor-dynamicList-pageInput"
          />

          <button
            class="tb-btn tb-btn-contained medium-em"
            data-test="graphics-editor-dynamicList-nextPage"
            :disabled="currentQuestionIndex >= questions.length"
            @click="currentQuestionIndex++"
          >
            <span class="fas fa-forward" />
          </button>
        </div>
      </div>
    </div>


    <template v-if="currentDisplayState === 'results'">
      <div class="tb-checkbox">
        <input
          type="checkbox"
          id="animateResults"
          v-model="animateResults"
          data-test="poll-editor-showAnimateControls"
        />

        <label class="checkbox" for="animateResults">Manually show results</label>
      </div>

      <div
        v-if="animateResults"
        class="action-key-container"
      >
        <label class="tb-label">
          Vote Animation Controls
        </label>

        <div class="action-keys">
          <action-key
            v-for="(action, index) in actions"
            :key="index"
            :action="action"
            :production_context="production_context"
            :data-test="`poll-editor-${action.hotkey.toLowerCase()}`"
          />
        </div>
      </div>
    </template>

    <portal to="editor-footer">
      <button
        class="tb-btn tb-btn-contained sm sample"
        :disabled="!isCurrentSampleLayout"
        data-entity="producer-editor-updatePreview"
        data-test="producer-editor-updatePreview"
        @click="updatePreviewTimeline({ force: true })"
      >
        Update Preview
      </button>

      <button
        class="tb-btn tb-btn-contained sm alarm"
        :disabled="!isCurrentLayout"
        data-entity="producer-editor-updateLive"
        data-test="producer-editor-updateLive"
        @click="updateTimeline({ force: true })"
      >
        Update Live
      </button>
    </portal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { debounce } from 'lodash-es';
import { Portal } from 'portal-vue';

import editingMixins from '../../mixins/editing-mixins';

import ActionKey from '../panel-editor/hotkeys/ActionKey.vue';

export default {
  name: 'PollEditor',
  mixins: [editingMixins],
  props: {
    item: {
      type: Object,
      required: true,
    },
    editing: {
      type: Object,
      required: true,
    },
  },
  components: {
    Portal,
    ActionKey,
  },

  data() {
    return {
      actions: [
        {
          hotkey: 'I',
          display: 'Animate In',
          description: 'Use this hotkey to animate results in',
          value: 'animate-results-in',
          type: 'poll',
        },
        {
          hotkey: 'O',
          display: 'Animate Out',
          description: 'Use this hotkey to animate results out',
          value: 'animate-results-out',
          type: 'poll',
        },
      ],
    };
  },

  computed: {
    ...mapState('polls', [
      'lookupTable',
      'isLoadingMap',
    ]),

    title: {
      get() {
        return this.item.title;
      },
      set: debounce(function(value) {
        this.setTimelineTitle({
          timelineId: this.editing?.timeline_id,
          title: value,
        });

        this.updateTimeline();
        this.updatePreviewTimeline();
      }, 500),
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      return this.item && (this.editing.timeline_id === this.currentLayoutIndex);
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      return this.item && (this.currentSampleLayoutIndex === this.editing.timeline_id);
    },

    production_context() {
      return { live: this.isCurrentLayout, preview: this.isCurrentSampleLayout };
    },

    poll() {
      return this.item && this.lookupTable[this.item.pollId];
    },

    display() {
      return this.poll?.displays?.find(display => display.displayId === this.item.displayId);
    },

    rounds() {
      return this.poll ? this.poll.rounds : [];
    },

    questions() {
      return this.poll ? this.poll.rounds[this.currentRoundIndex].questions : [];
    },

    stateLabels() {
      if (this.display.displayType === 'poll') {
        return {
          preVote: 'Open',
          results: 'Post Vote',
          closed: 'Closed',
        };
      }

      return {
        preVote: 'Open',
        results: 'Results',
        closed: 'Final',
      };
    },

    pollStates() {
      const sortingArray = ['preVote', 'results', 'closed'];

      const { defaults, displaySettings, popUp, ...rest } = this.display.settings;
      return Object.keys(rest).sort((a, b) => sortingArray.indexOf(a) - sortingArray.indexOf(b));
    },

    pollFields() {
      return {
        ...this.editing,
        ...this.editing.fields,
      };
    },

    currentRoundIndex: {
      get() {
        return this.pollFields.currentRoundIndex || 0;
      },
      set(value) {
        this.setField('currentRoundIndex', value);
      },
    },

    currentDisplayState: {
      get() {
        return this.pollFields.currentDisplayState || 'results';
      },
      set(value) {
        this.setField('currentDisplayState', value);
      },
    },

    currentQuestionIndex: {
      get() {
        const idx = this.pollFields.currentQuestionIndex || 0;
        return idx + 1; // Adjust for 1-based index in UI
      },
      set(value) {
        const idx = value - 1; // Adjust for 0-based index in logic
        this.setField('currentQuestionIndex', idx);
      },
    },

    animateResults: {
      get() {
        return this.pollFields.animateResults || false;
      },
      set(value) {
        this.setField('animateResults', value);
      },
    },
  },

  methods: {
    ...mapActions([
      'setTimelineTitle',
      'updateTimeline',
      'updatePreviewTimeline',
    ]),
  },
};
</script>

<style scoped lang="scss">
.poll-state {
  &__label {
    padding: 0.5rem 1rem;
    cursor: pointer;
    color: variables.$color-on-surface-high;
    border: 1px solid rgba(255, 255, 255, 0.10);
    border-radius: 0;
    background-color: variables.$color-12dp;

    &:first-child {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }

    &:last-child {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }

    &--active {
      background-color: rgba(255, 255, 255, 0.25);
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
  }
}
</style>
